import { select } from 'd3-selection';
import { line } from 'd3-shape';
import { useEffect, useRef } from 'react';
import { convertBeatTypeNumberToColor, convertBeatTypeNumberToText } from '@/shared/ui';
import {
  calculateConfig,
  calculatePixelSamples,
  calculateSamples,
  CONFIG_ECG,
} from '@/entities/ecg/@x/event';
import type { ThumbnailEventProps } from './thumbnailEvent';

/**
 * Draws a small ECG strip thumbnail for an event into an SVG element. Builds a display config from gain, speed, size and the high-pass filter, converts the selected channels' samples to pixel coordinates, and on every data or setting change clears the SVG and redraws one path per channel plus a colour-coded beat-type letter above each beat marker.
 *
 * @param props - The ECG samples, channels, gain, speed, size, high-pass filter, beat markers and beat statuses to draw.
 * @returns An object carrying canvasRef for the SVG element.
 */
const useThumbnailEvent = ({
  ecgData,
  channels = [2],
  gain,
  speed,
  width,
  height,
  highPass,
  beatMarkers = [],
  hesBeatStatus = [],
}: Pick<
  ThumbnailEventProps,
  | 'ecgData'
  | 'channels'
  | 'gain'
  | 'speed'
  | 'width'
  | 'height'
  | 'highPass'
  | 'beatMarkers'
  | 'hesBeatStatus'
>) => {
  const canvasRef = useRef<SVGSVGElement>(null);
  const drawStrip = ({
    ecgData,
    beatMarkers,
    hesBeatStatus,
    channels = [],
  }: {
    ecgData: number[][];
    beatMarkers: number[];
    hesBeatStatus: number[];
    channels?: number[];
  }) => {
    if (channels.length === 0) {
      return;
    }
    const countChannels = ecgData.length;
    const formatChannels = channels.map((x) => x - 1);
    const drawEcgData: number[][] = [];
    if (countChannels > 1) {
      formatChannels.forEach((x) => {
        drawEcgData.push(ecgData[x]);
      });
    } else {
      drawEcgData.push(ecgData[0]);
    }
    let formatHeight = height;
    if (drawEcgData.length > 1) {
      formatHeight -= 10;
    }
    // calculateConfig's JSDoc param type (BeatHR/helper, not yet converted) also
    // declares a pHFF field this call site never passed.
    const config = calculateConfig({
      pGain: gain,
      pSpeed: speed,
      width,
      height: formatHeight,
      adcGain: CONFIG_ECG.gain,
      ecgData: drawEcgData,
      samplingRate: CONFIG_ECG.samplingFrequency,
      pLFF: highPass,
    } as any);
    const samples = calculateSamples(drawEcgData, config);
    const pixelSamples = calculatePixelSamples(samples, config);
    const { indexToPix } = config;
    const pixelBeats = beatMarkers.map((beatMarker) => Math.floor(beatMarker * indexToPix));

    if (canvasRef.current) {
      const svg = select(canvasRef.current);
      svg.selectAll('*').remove();
      const lineFunc = line<{ x: number; y: number }>()
        .x((d) => d.x)
        .y((d) => d.y);
      pixelSamples.forEach((displayedSamples: any) => {
        svg
          .append('path')
          .attr('d', lineFunc(displayedSamples))
          .attr('stroke', '#1D2F47')
          .attr('stroke-width', '1.2px')
          .attr('fill', 'none');
      });
      pixelBeats.forEach((pixelBeat, index) => {
        svg
          .append('text')
          .attr('x', pixelBeat)
          .attr('y', 10)
          .attr('text-anchor', 'middle')
          .style('font-size', '10px')
          .style('font-weight', '600')
          .style('fill', convertBeatTypeNumberToColor(hesBeatStatus[index]))
          .text(convertBeatTypeNumberToText(hesBeatStatus[index]));
      });
    }
  };

  useEffect(() => {
    if (ecgData?.length > 0) {
      drawStrip({
        ecgData,
        beatMarkers,
        hesBeatStatus,
        channels,
      });
    }
  }, [
    ecgData,
    JSON.stringify(channels),
    gain,
    highPass,
    width,
    height,
    beatMarkers,
    hesBeatStatus,
  ]);

  return { canvasRef };
};

export default useThumbnailEvent;
